import React, { useCallback, useState } from 'react';
import Modal from 'react-modal';
import Input from '../../../components/Input/Input';
import Button from '../../../components/Button/Button';
import { IoClose } from 'react-icons/io5';
import './ResponsibleModal.css';
import { customStyles } from '../../../components/Modal/ModalComponent';
import { removeSpecialChars } from '../../../utils/string';
import OperadorService from '../../../services/OperadorService';
import { useAuth } from '../../../contexts/auth';
import { debounce } from '../../../utils/timer';
import { FiUserPlus } from 'react-icons/fi';
import { LuSearch } from 'react-icons/lu';
import { AnimatedTabbar, AnimatedTabbarItem } from '../../../components/AnimatedTabbar/AnimatedTabbar';
import { v4 as uuidv4 } from 'uuid';
import ToggleButton from '../../../components/ToggleButton/ToggleButton';

// Make sure to bind modal to your appElement (http://reactcommunity.org/react-modal/accessibility/)
Modal.setAppElement('#root'); // Assuming root id is 'root'

const MIN_CHARS_TO_SEARCH = 3;

function ResponsibleModal({ isOpen, onClose, onAdd }) {
	const [activeTab, setActiveTab] = useState('cadastro'); // 'cadastro' or 'buscar'
	const { user } = useAuth();

	// Cadastro State
	const [nome, setNome] = useState('');
	const [email, setEmail] = useState('');
	const [cpf, setCpf] = useState('');
	const [phone, setPhone] = useState(''); // Optional if differentiating email/phone
	const [createUser, setCreateUser] = useState(false);

	const [isLoading, setIsLoading] = useState(false);
	const fetchPersonsDebounce = useCallback(
		debounce(fetchPersons, 700), []
	);

	// Buscar State
	const [searchTerm, setSearchTerm] = useState('');
	const [searchResults, setSearchResults] = useState([]); // Mocked for now
	const [selectedResult, setSelectedResult] = useState(null);

	const resetForm = () => {
		setNome('');
		setEmail('');
		setCpf('');
		setPhone('');
		setSearchTerm('');
		setSearchResults([]);
		setSelectedResult(null);
	};

	const handleClose = () => {
		resetForm();
		onClose();
	};

	const handleCadastrar = () => {
		if (!nome || !cpf || !email) {
			alert("Por favor, preencha todos os campos obrigatórios.");
			return;
		}

		// Simple validation logic (can be expanded)
		const newResponsible = {
			name: nome,
			document: cpf,
			email: email,
			phone: phone,
			externalId: uuidv4(),
			createUser: createUser,
		};

		onAdd(newResponsible);
		handleClose();
	};

	async function fetchPersons(searchTerm) {
		setIsLoading(true);
		const { data } = await OperadorService.listClientOperadoresPaginated(
			1,
			searchTerm,
			user.userData.selectedOrganizationId,
			{
				collumnTitle: 'name',
				orderDirection: 'ASC',
			}
		);
		setIsLoading(false);
		const results = data.items?.filter(r => removeSpecialChars(r.name).toLowerCase().includes(removeSpecialChars(searchTerm).toLowerCase()));
		setSearchResults(results);
	}

	const handleSearch = async (searchTerm) => {
		setSearchResults(null);
		if (!searchTerm || searchTerm.length < MIN_CHARS_TO_SEARCH) {
			return;
		}
		fetchPersonsDebounce(searchTerm);
	};

	const handleSelectResult = (res) => {
		setSelectedResult(res);
	};

	const handleAddSelected = () => {
		if (selectedResult) {
			onAdd(selectedResult);
			handleClose();
		}
	};

	function getPersonsResult() {
		if (isLoading) {
			return <p style={{ padding: '10px', textAlign: 'center' }}>Buscando...</p>;
		}
		if ((searchResults == null || !searchTerm || searchTerm.length < MIN_CHARS_TO_SEARCH)) {
			return <p style={{ padding: '10px', textAlign: 'center' }}>Digite ao menos {MIN_CHARS_TO_SEARCH} caracteres para iniciar a busca.</p>;
		}
		if (searchResults?.length === 0 && searchTerm && searchTerm.length >= MIN_CHARS_TO_SEARCH) {
			return <p style={{ padding: '10px', textAlign: 'center' }}>Nenhum resultado.</p>;
		}

		return searchResults?.map((res, idx) => (
			<button
				type='button'
				key={idx}
				className={`search-item ${selectedResult === res ? 'selected' : ''}`}
				onClick={() => handleSelectResult(res)}
			>
				<strong>{res.name}</strong>
				<span style={{ fontSize: '0.9em', marginTop: '0.3em' }}>{[res.email, res.phone].filter(i => i && i !== '').join(' - ')}</span>
			</button>
		))
	}

	return (
		<Modal
			isOpen={isOpen}
			onRequestClose={handleClose}
			className="responsible-modal-content"
			overlayClassName="responsible-modal-overlay"
			style={customStyles}
			contentLabel="Adicionar Responsável"
		>
			<div className="modal-header border-none! mb-0!">
				<h2>Adicionar Responsável</h2>
				<button className="close-button" onClick={handleClose}><IoClose /></button>
			</div>

			<div className="modal-tabs" style={{ gap: 25 }}>
				<AnimatedTabbar
					value={activeTab}
					onChange={setActiveTab}
				>
					<AnimatedTabbarItem name="cadastro">
						<FiUserPlus className='inline-flex mr-1!' />
						Novo Cadastro
					</AnimatedTabbarItem>
					<AnimatedTabbarItem name="buscar">
						<LuSearch className='inline-flex mr-1!' />
						Buscar
					</AnimatedTabbarItem>
				</AnimatedTabbar>
			</div>

			<div className="modal-body">
				{activeTab === 'cadastro' && (
					<>
						<Input
							name="modal_nome"
							id="modal_nome"
							labelText="Nome *"
							value={nome}
							onChange={(e) => setNome(e.target.value)}
							required
						/>
						<Input
							name="modal_cpf"
							id="modal_cpf"
							labelText="CPF *"
							value={cpf}
							onChange={(e) => setCpf(e.target.value)}
							required
							showCpfMask={true}
							maxLength="14"
						/>
						<Input
							name="modal_email"
							id="modal_email"
							labelText="Email *"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							required
						/>
						<Input
							name="modal_phone"
							id="modal_phone"
							labelText="Contato *"
							value={phone}
							onChange={(e) => setPhone(e.target.value)}
							required
						/>
						<ToggleButton
							label="Criar Usuário Cloud"
							defaultChecked={createUser}
							onChange={({ target }) => setCreateUser(target.checked)}
							required={false}
							tooltipText={
								"Criar usuário na plataforma com as informações fornecidas"
							}
							tooltipVisible={true}
						/>
					</>
				)}

				{activeTab === 'buscar' && (
					<>
						<div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
							<div style={{ flexGrow: 1 }}>
								<Input
									name="modal_search"
									id="modal_search"
									labelText="Buscar por Nome"
									value={searchTerm}
									onChange={(e) => {
										const value = e.target.value;
										setSearchTerm(value);
										handleSearch(value);
									}}
								/>
							</div>
						</div>

						<div className="search-results">
							{getPersonsResult()}
						</div>
					</>
				)}
			</div>

			<div className="modal-footer">
				<Button className="button-white" label="Cancelar" onClick={handleClose} />
				{activeTab === 'cadastro' ? (
					<Button label="Adicionar" onClick={handleCadastrar} />
				) : (
					<Button label="Adicionar Selecionado" onClick={handleAddSelected} disabled={!selectedResult} />
				)}
			</div>
		</Modal>
	);
}

export default ResponsibleModal;
